import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, GeoJSON, useMap } from 'react-leaflet';
import L, { map } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './EnergyProjectsMap.scss';
import { API_BASE } from '../../config/environments/api';

// Fix for default markers in react-leaflet
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});
const isMobile = window.innerWidth <= 480;
const EnergyProjectsMap = () => {
  const [activeFilter, setActiveFilter] = useState('Combined');
  const [africaCountries, setAfricaCountries] = useState([]);
  const [africaGeoJSON, setAfricaGeoJSON] = useState(null);
  const [projectData, setProjectData] = useState([]);
  const [segments, setSegments] = useState([]);
  const [selectedSegments, setSelectedSegments] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [markersLoading, setMarkersLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showCustomPopup, setShowCustomPopup] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const mapRef = useRef(null);
  const segmentsInitialized = useRef(false);
  const markerGroupRef = useRef(null);

  const filters = ['Combined', 'World Bank', 'AfDB'];

  // Function to center the map
  const centerMap = () => {
    if (mapRef.current) {
      // Match original map config with slightly bigger zoom
      mapRef.current.setView([0, 20], 3); // Same as initial zoom but centered
    }
  };

  // Handle marker click to show custom popup - memoized to prevent unnecessary re-renders
  const handleMarkerClick = useCallback((project) => {
    setSelectedProject(project);
    setShowCustomPopup(true);
  }, []);

  // Handle markers loaded callback
  const handleMarkersLoaded = useCallback(() => {
    setMarkersLoading(false);
  }, []);

  // Close custom popup with animation
  const closeCustomPopup = () => {
    setIsClosing(true);
    setTimeout(() => {
      setShowCustomPopup(false);
      setSelectedProject(null);
      setIsClosing(false);
    }, 400); // Match animation duration
  };

  // Handle segment selection
  const handleSegmentToggle = (segment) => {
    setSelectedSegments(prev => {
      if (prev.includes(segment)) {
        return prev.filter(s => s !== segment);
      } else {
        return [...prev, segment];
      }
    });
  };

  // Handle select all segments
  const handleSelectAll = () => {
    setSelectedSegments(segments);
  };

  // Handle deselect all segments
  const handleDeselectAll = () => {
    setSelectedSegments([]);
  };

  // Load Africa GeoJSON data and project data
  useEffect(() => {
    // Load Africa GeoJSON with detailed country boundaries
    fetch('/map/africa.geo.json')
      .then(response => response.json())
      .then(data => {
        setAfricaGeoJSON(data);
        // Extract country coordinates from GeoJSON for project positioning
        extractCountryCoordinates(data);
      })
      .catch(error => {});

    // Load project data from API
    fetchProjectData();
  }, []);

  // Close popup when filter toggle changes (but don't reset segments since they're the same for all filters)
  useEffect(() => {
    // Close popup immediately when filter changes
    setShowCustomPopup(false);
    setSelectedProject(null);
    setIsClosing(false);
    // Don't clear segments - they're the same for all filters now
  }, [activeFilter]);


  // Fetch project data from API using POST with reporting_org filtering
  const fetchProjectData = async (filter = 'Combined') => {
    try {
      setLoading(true);
      setMarkersLoading(true); // Start markers loading
      const url = `${API_BASE}/projects/map`;

      // Prepare request body with reporting_org_filter
      const requestBody = {
        reporting_org_filter: filter // 'Combined', 'AfDB', 'World Bank'
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();

      if (result.success) {
        setProjectData(result.data);
        const newSegments = result.segments || [];
        
        // Only update segments on initial load (first time)
        // Since all filters have the same segments, don't update on filter changes
        if (!segmentsInitialized.current) {
          setSegments(newSegments);
          // Select all segments on initial load
          setSelectedSegments(newSegments);
          segmentsInitialized.current = true;
        }
        // Don't update segments or selectedSegments when filter changes - keep existing selections
      } else {
        setMarkersLoading(false); // Stop loading on error
      }
    } catch (error) {
      setMarkersLoading(false); // Stop loading on error
    } finally {
      setLoading(false);
      // markersLoading will be set to false after markers are rendered
    }
  };

  // Extract country coordinates from GeoJSON for project positioning
  const extractCountryCoordinates = (geoJsonData) => {
    const countryCoordinates = {};

    geoJsonData.features.forEach(feature => {
      const countryName = feature.properties.name;
      if (feature.geometry && feature.geometry.coordinates) {
        let allCoordinates = [];

        // Handle different geometry types
        if (feature.geometry.type === 'Polygon') {
          // For Polygon, coordinates is an array of rings
          feature.geometry.coordinates.forEach(ring => {
            allCoordinates = allCoordinates.concat(ring);
          });
        } else if (feature.geometry.type === 'MultiPolygon') {
          // For MultiPolygon, coordinates is an array of polygons
          feature.geometry.coordinates.forEach(polygon => {
            polygon.forEach(ring => {
              allCoordinates = allCoordinates.concat(ring);
            });
          });
        }

        if (allCoordinates.length > 0) {
          // Calculate center point
          let sumLat = 0, sumLng = 0;
          allCoordinates.forEach(coord => {
            if (Array.isArray(coord) && coord.length >= 2) {
              sumLng += parseFloat(coord[0]) || 0;
              sumLat += parseFloat(coord[1]) || 0;
            }
          });

          const centerLat = sumLat / allCoordinates.length;
          const centerLng = sumLng / allCoordinates.length;

          // Only add if coordinates are valid numbers
          if (!isNaN(centerLat) && !isNaN(centerLng)) {
            countryCoordinates[countryName] = [centerLat, centerLng];
          }
        }
      }
    });

    setAfricaCountries(countryCoordinates);
  };

  // Africa GeoJSON styling to match screenshot
  const africaStyle = (feature) => {
    return {
      fillColor: '#f5f5f5', // Light gray background
      weight: 1,
      opacity: 1,
      color: '#d17c1b', // Orange border
      dashArray: '2,2', // Dashed border
      fillOpacity: 0.8
    };
  };

  // Function to style Africa GeoJSON
  const onEachFeature = (feature, layer) => {
    if (feature.properties && feature.properties.name) {
      layer.bindPopup(`
        <div class="country-popup">
          <h4>${feature.properties.name}</h4>
          <p>Click markers to view energy projects</p>
        </div>
      `);
    }
  };

  // Update project coordinates from GeoJSON data
  const updateProjectCoordinates = (projects, countryCoords) => {
    return projects.map(project => {
      const countryCoord = countryCoords[project.country];
      if (countryCoord) {
        return { ...project, coordinates: countryCoord };
      }
      return project;
    });
  };

  // Helper function to extract year from date string (handles various formats)
  const extractYearFromDate = (dateString) => {
    if (!dateString) return 'N/A';

    // Handle ISO date strings from backend (e.g., "2025-09-03T00:00:00.000Z")
    if (typeof dateString === 'string') {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.getFullYear();
      }

      // Try parsing M/D/YYYY format manually
      const mdyMatch = dateString.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
      if (mdyMatch) {
        const [, month, day, year] = mdyMatch;
        const parsedDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        if (!isNaN(parsedDate.getTime())) {
          return parsedDate.getFullYear();
        }
      }

      // Try parsing YYYY-MM-DD format manually
      const ymdMatch = dateString.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
      if (ymdMatch) {
        const [, year, month, day] = ymdMatch;
        const parsedDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        if (!isNaN(parsedDate.getTime())) {
          return parsedDate.getFullYear();
        }
      }
    }

    // Handle Date objects
    if (dateString instanceof Date) {
      if (!isNaN(dateString.getTime())) {
        return dateString.getFullYear();
      }
    }

    return 'N/A';
  };

  // Memoize processed projects to avoid unnecessary recalculations
  const processedProjects = useMemo(() => {
    return projectData.map(project => {
    // Prioritize country coordinates from GeoJSON for consistent positioning
    let coordinates = null;
    if (africaCountries[project.country] &&
      Array.isArray(africaCountries[project.country]) &&
      africaCountries[project.country].length === 2) {
      coordinates = africaCountries[project.country];
    } else if (project.coordinates &&
      project.coordinates.latitude &&
      project.coordinates.longitude) {
      coordinates = [project.coordinates.latitude, project.coordinates.longitude];
    }

    // Use segment directly for icon mapping
    const segment = project.segments || 'Energy';

    // Handle the new API format with renamed fields
    const impactBeneficiaries = project.impact_beneficiaries || '';
    const totalCommitments = typeof project.investment === 'number'
      ? project.investment
      : parseFloat(project.investment) || 0.0;

    // Process only projects with valid data
    if (!project.project_Name || project.project_Name.trim() === '') {
      return null; // Skip projects without titles
    }

    return {
      id: project._id,
      name: project.project_Name,
      country: project.country,
      type: project.segments || 'Energy',
      funder: project.reporting_org || 'Unknown',
      year: extractYearFromDate(project.activity_date_actual_start),
      investment: totalCommitments > 0 ? `$${totalCommitments.toLocaleString()}` : 'N/A',
      impact: impactBeneficiaries && impactBeneficiaries.trim() !== '' ? [impactBeneficiaries] : ['Impact data not available'],
      impact_beneficiaries: impactBeneficiaries,
      total_commitments: totalCommitments,
      coordinates: coordinates,
      segment: segment
    };
  }).filter(project => project && project.coordinates && project.name); // Only include projects with valid data
  }, [projectData, africaCountries]);

  // Memoize filtered projects
  const filteredProjects = useMemo(() => {
    return processedProjects.filter(project =>
      selectedSegments.includes(project.segment)
    );
  }, [processedProjects, selectedSegments]);

  // Component to update markers without re-rendering map
  const MarkerLayer = React.memo(({ projects, onMarkerClick, onMarkersLoaded }) => {
    const map = useMap();
    
    useEffect(() => {
      // Clear existing markers immediately (no fade out)
      if (markerGroupRef.current) {
        map.removeLayer(markerGroupRef.current);
        markerGroupRef.current = null;
      }
      
      // Create new marker group
      const markerGroup = L.layerGroup();
      
      // Add markers for valid projects
      const validProjects = projects.filter(project =>
        project.coordinates &&
        Array.isArray(project.coordinates) &&
        project.coordinates.length === 2 &&
        !isNaN(project.coordinates[0]) &&
        !isNaN(project.coordinates[1])
      );
      
      validProjects.forEach((project) => {
        const icon = createCustomIcon(project.segment);
        const marker = L.marker(project.coordinates, { icon });
        
        marker.on('click', () => {
          onMarkerClick(project);
        });
        
        markerGroup.addLayer(marker);
      });
      
      // Add new marker group to map
      markerGroup.addTo(map);
      markerGroupRef.current = markerGroup;
      
      // Notify that markers are loaded
      if (onMarkersLoaded) {
        // Small delay to ensure map has rendered markers
        setTimeout(() => {
          onMarkersLoaded();
        }, 100);
      }
      
      // Cleanup function
      return () => {
        if (markerGroupRef.current && map.hasLayer(markerGroupRef.current)) {
          // Remove immediately without fade out
          map.removeLayer(markerGroupRef.current);
          markerGroupRef.current = null;
        }
      };
    }, [projects, map, onMarkerClick, onMarkersLoaded]);
    
    return null;
  }, (prevProps, nextProps) => {
    // Only re-render if projects array actually changed
    if (prevProps.projects.length !== nextProps.projects.length) {
      return false; // Re-render needed
    }
    // Compare project IDs to see if content changed
    const prevIds = prevProps.projects.map(p => p.id).sort().join(',');
    const nextIds = nextProps.projects.map(p => p.id).sort().join(',');
    return prevIds === nextIds; // true = skip re-render, false = re-render
  });
  
  MarkerLayer.displayName = 'MarkerLayer';

  // Set default selected project

  // Comprehensive icon mapping for all available icons
  const getIconForSegment = (segment) => {
    if (!segment || typeof segment !== 'string') return '/images/map/mdi_energy-circle.svg';

    const segmentLower = segment.toLowerCase();

    // Map segments to specific icons as requested
    if (segmentLower.includes('transmission infrastructure for energy access')) {
      return '/images/map/transmission-line-energy.png';
    } else if (segmentLower.includes('transmission infrastructure')) {
      return '/images/map/transmission-infra.png';
    } else if (segmentLower.includes('transmission')) {
      return '/images/map/transmission-line.png';
    } else if (segmentLower.includes('scale up renewable energy and clean transport')) {
      return '/images/map/renewable-energy-and-clean-transport.png';
    } else if (segmentLower.includes('scale up renewable energy')) {
      return '/images/map/scale-up-renewable-energy.png';
    } else if (segmentLower.includes('renewable energy')) {
      return '/images/map/renewable.png';
    } else if (segmentLower.includes('solar')) {
      return '/images/map/mdi_solar-energy-outline.svg';
    } else if (segmentLower.includes('wind')) {
      return '/images/map/mdi_wind-electricity-outline.svg';
    } else if (segmentLower.includes('lighting') || segmentLower.includes('light')) {
      return '/images/map/energy-efficiency.png';
    } else if (segmentLower.includes('cooking') || segmentLower.includes('clean cooking')) {
      return '/images/map/clean-cooking.png';
    } else if (segmentLower.includes('electricity') || segmentLower.includes('energy access')) {
      return '/images/map/energy-access.png';
    } else if (segmentLower.includes('efficiency') || segmentLower.includes('energy efficiency')) {
      return '/images/map/energy-efficiency.png';
    } else if (segmentLower.includes('finance') || segmentLower.includes('financial')) {
      return '/images/map/finance.png';
    } else if (segmentLower.includes('infrastructure')) {
      return '/images/map/infrastructure.png';
    } else if (segmentLower.includes('policy') || segmentLower.includes('technical')) {
      return '/images/map/protected.png';
    } else if (segmentLower.includes('scale up') || segmentLower.includes('scale-up')) {
      return '/images/map/scale-up.png';
    } else if (segmentLower.includes('transport') || segmentLower.includes('vehicle')) {
      return '/images/map/electric-vehicle.png';
    } else if (segmentLower.includes('guarantee') || segmentLower.includes('guaranteed')) {
      return '/images/map/guaranteed.png';
    } else if (segmentLower.includes('protected') || segmentLower.includes('protection')) {
      return '/images/map/protected.png';
    } else if (segmentLower.includes('fossil fuel') || segmentLower.includes('fossil fuel')) {
      return '/images/map/electricity-fossil-fuel.png';
    } else {
      return '/images/map/n-a.png'; // Default icon
    }
  };

  // Custom marker icons using all available icons
  const createCustomIcon = (segment) => {
    const iconSrc = getIconForSegment(segment);

    return L.divIcon({
      className: 'custom-marker',
      html: `<div class="marker-container" style="background: white; border: 2px solid #d17c1b; border-radius: 50%; width: 20px; height: 20px; display: flex; align-items: center; justify-content: center; position: relative;">
        <img src="${iconSrc}" alt="${segment}" class="marker-icon" style="width: 12px; height: 12px; object-fit: contain; filter: drop-shadow(0 1px 2px rgba(0,0,0,0.2));" />
      </div>`,
      iconSize: [24, 24],
      iconAnchor: [16, 16]
    });
  };

  return (
    <section className="energy-projects-section">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">Energy Projects <span>Across Africa</span></h2>
          <p className="section-subtitle">
            Discover the geographic footprint of renewable energy access efforts—from national grids to off-grid innovations.
          </p>
        </div>

        <div className="map-container">
          <div className="map-content">
            {/* Left Side - Map */}
            <div className="map-section">
              <div className="main-filter-box">
                <h3 className="map-subtitle">Energy Projects by Segment</h3>
                <div className="chart-filters">
                  {filters.map(filter => (
                    <button
                      key={filter}
                      className={`filter-btn ${activeFilter === filter ? 'active' : ''}`}
                      onClick={() => {
                        setActiveFilter(filter);
                        fetchProjectData(filter);
                        // Don't center map - it causes re-render, just update markers
                      }}
                    >
                      {filter}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Map and Legend Side by Side */}
            <div className="map-legend-container">
              <div className="leaflet-map-container" style={{ position: 'relative' }}>
                {/* Page Loader - Shows during initial load and filter changes */}
                {(loading || markersLoading) && (
                  <div className="app-loader">
                    <div className="loader-container">
                      <img 
                        src="/spinner/circle-spinner.gif" 
                        alt="Loading..." 
                        className="loader-spinner"
                      />
                    </div>
                  </div>
                )}
                {/* MapContainer - always rendered, never unmounts */}
                <MapContainer
                  key="energy-projects-map"
                  ref={mapRef}
                  //center={isMobile ? [5, 15] : [0, 22]}
                  // zoom={isMobile ? 2.5 : 3.4}
                  // minZoom={2}
                  // maxZoom={6}
                  // style={{ height: '500px', width: '100%' }}
                  zoom={isMobile ? 4.5 : 3.4} 
                  minZoom={isMobile ? 2.3 : 2.3}
                  maxZoom={isMobile ? 6 : 6}
                  style={{ 
                    height: isMobile ? '400px' : '500px', 
                    width: isMobile ? '100%' : '100%', 
                    minWidth: isMobile ? '100%' : '500px', 
                    maxWidth: isMobile ? '100%' : '100%' 
                  }}
                  zoomControl={isMobile ? true : false}
                  attributionControl={isMobile ? true : false}
                  scrollWheelZoom={false}
                  dragging={isMobile ? true : false}
                  bounds={[[-37, -20], [40, 55]]}
                  boundsOptions={{ padding: [10, 10] }}
                  filterBounds={[[-30, -10], [36, 50]]}
                >
                  {/* Africa GeoJSON layer with detailed country boundaries */}
                  {africaGeoJSON && (
                    <GeoJSON
                      data={africaGeoJSON}
                      style={africaStyle}
                      onEachFeature={onEachFeature}
                    />
                  )}

                  {/* Project markers layer - only updates markers, not the entire map */}
                  <MarkerLayer 
                    projects={filteredProjects} 
                    onMarkerClick={handleMarkerClick}
                    onMarkersLoaded={handleMarkersLoaded}
                  />
                </MapContainer>

                {/* Custom Popup - Normal Position on Right Side of Map Container */}
                {showCustomPopup && selectedProject && (
                  <div className={`custom-popup-overlay ${isClosing ? 'closing' : ''}`} onClick={closeCustomPopup}>
                    <div
                      className={`custom-popup ${isClosing ? 'closing' : ''}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="popup-close-btn" onClick={closeCustomPopup}>
                        <img src="/images/map/popup-close-btn.svg" alt="Close" />
                      </div>
                      <div className="project-details-card">
                        <h3 className="project-title">{selectedProject.name}</h3>
                        <p className="project-country"><span style={{ color: "#333" }}>Country:</span> {selectedProject.country}</p>

                        <div className="project-info">
                          <div className="info-item">
                            <span className="info-label">Segment:</span>
                            <span className="info-value">{selectedProject.type}</span>
                          </div>
                          <div className="info-item">
                            <span className="info-label">Funder:</span>
                            <span className="info-value">{selectedProject.funder}</span>
                          </div>
                          <div className="info-item">
                            <span className="info-label">Year:</span>
                            <span className="info-value">{selectedProject.year}</span>
                          </div>
                          <div className="info-item">
                            <span className="info-label">Investment:</span>
                            <span className="info-value">{selectedProject.total_commitments > 0 ? `$${selectedProject.total_commitments.toLocaleString()}` : 'N/A'}</span>
                          </div>
                        </div>

                        {/* Only show Impact section if there's actual impact data */}
                        {selectedProject.impact_beneficiaries && selectedProject.impact_beneficiaries.trim() !== '' && (
                          <div className="project-impact">
                            <h4 className="impact-title">Impact:</h4>
                            <ul className="impact-list">
                              <li className="impact-item">
                                <img src="/images/map/pointer.svg" alt="Pointer" className="impact-pointer" />
                                {selectedProject.impact_beneficiaries}
                              </li>
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Segment Filter Dropdown */}
              <div className="segment-filter-dropdown">
                <div className="dropdown-header">
                  <span className="dropdown-title">
                    Filter by Segments ({selectedSegments.length}/{segments.length})
                  </span>

                  <div className="dropdown-actions">
                    <button
                      className="select-all-btn"
                      onClick={handleSelectAll}
                      disabled={selectedSegments.length === segments.length}
                    >
                      Select All
                    </button>
                    <button
                      className="deselect-all-btn"
                      onClick={handleDeselectAll}
                      disabled={selectedSegments.length === 0}
                    >
                      Deselect All
                    </button>
                  </div>

                </div>


                <div className="dropdown-content">
                  <div className="segment-options">
                    {segments.sort().map((segment) => {
                      const iconSrc = getIconForSegment(segment);
                      const isSelected = selectedSegments.includes(segment);

                      return (
                        <div
                          key={segment}
                          className={`segment-option ${isSelected ? 'selected' : ''}`}
                          onClick={() => handleSegmentToggle(segment)}
                        >
                          <div className="checkbox-container">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleSegmentToggle(segment)}
                              className="segment-checkbox"
                              onClick={(e) => e.stopPropagation()}
                            />
                            <div
                              className="segment-icon"
                              style={{
                                background: 'white',
                                border: '2px solid #d17c1b',
                                borderRadius: '50%',
                                width: '20px',
                                height: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                marginRight: '8px'
                              }}
                            >
                              <img
                                src={iconSrc}
                                alt={segment}
                                style={{
                                  width: '12px',
                                  height: '12px',
                                  objectFit: 'contain',
                                  filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.1))'
                                }}
                              />
                            </div>
                          </div>
                          <span className="segment-label" onClick={() => handleSegmentToggle(segment)}>{segment}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Toggle Buttons */}
          </div>
        </div>
      </div>


    </section>
  );
};

export default EnergyProjectsMap;



